import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Calendar, Clock, User, Phone, Mail, Navigation, Camera, Video, Film, ChevronDown, Download, Star, Check, Upload, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { format } from "date-fns";
import { toast } from "sonner";
import { PageHeader } from "@/components/navigation/PageHeader";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { StepProgressBar } from "@/components/tracking/StepProgressBar";
import { loadGoogleMapsLibrary } from "@/lib/google-maps-loader";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { useFilmmakerLocation } from "@/hooks/useFilmmakerLocation";
import { useRouteCalculation } from "@/hooks/useRouteCalculation";
import { DynamicMap } from "@/components/tracking/DynamicMap";

export default function JobDetails() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(99); // Mock timer for en_route
  const [sessionProgress, setSessionProgress] = useState(0); // 0-100
  const [elapsedMinutes, setElapsedMinutes] = useState(0); // Real-time elapsed minutes
  const [deliverables, setDeliverables] = useState({
    photos: false,
    video: false,
    business360: false,
  });
  const mapRefAssigned = useRef<HTMLDivElement>(null);
  const mapRefEnRoute = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId || !user?.id) {
        setLoading(false);
        return;
      }

      // Fetch from Edge Function (to get verification_code)
      try {
        const { data, error } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId,
          userId: user.id,
          phone: user.phone
        });

        if (error) {
          console.error('Error fetching booking:', error);
          setBooking(null);
        } else if (data?.booking) {
          setBooking(data.booking as Booking);
        } else {
          console.error('No booking data returned');
          setBooking(null);
        }
      } catch (error) {
        console.error('Error loading booking:', error);
        setBooking(null);
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [user, bookingId]);

  // Função chamada quando Realtime detecta mudança
  const handleRealtimeUpdate = useCallback(async (updatedBooking: Booking | null) => {
    if (!bookingId || !user) return;

    // Se receber null, significa que precisa recarregar do servidor
    if (updatedBooking === null) {
      // Recarregar booking via Edge Function (to get verification_code)
      try {
        const { data, error } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId,
          userId: user.id,
          phone: user.phone
        });

        if (!error && data?.booking) {
          const newBooking = data.booking as Booking;
          setBooking(newBooking);
        }
      } catch (error) {
        console.error('Error reloading booking:', error);
      }
      return;
    }

    // Mock mode ou booking recebida diretamente
    setBooking(updatedBooking);
  }, [bookingId, user]);

  // Usar hook Realtime para escutar mudanças
  useBookingRealtime({
    bookingId: booking?.id || bookingId, // Preferir booking.id (UUID real), fallback para bookingId do route
    onUpdate: handleRealtimeUpdate,
    enabled: !!booking && !loading, // Só habilitar quando booking estiver carregado
  });

  // Helper function to wait for Google Maps Map class to be available
  const waitForGoogleMapsMap = useCallback(async (maxRetries = 10, retryDelay = 100): Promise<void> => {
    let retries = 0;
    while (!window.google?.maps?.Map && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      retries++;
    }
    
    if (!window.google?.maps?.Map) {
      throw new Error("Google Maps Map class not available after loading");
    }
  }, []);

  // Initialize Google Map for assigned status
  useEffect(() => {
    if (!mapRefAssigned.current || !booking || booking.status !== 'assigned') return;

    const createMap = () => {
      const destination = {
        lat: booking.lat || 43.6532,
        lng: booking.lng || -79.3832,
      };

      // Get Map ID from environment or use default
      const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

      const map = new window.google.maps.Map(mapRefAssigned.current!, {
        center: destination,
        zoom: 15,
        disableDefaultUI: false,
        zoomControl: true,
        mapId, // Required for AdvancedMarkerElement
      });

      // Load marker library and use AdvancedMarkerElement
      window.google.maps.importLibrary('marker').then((markerLib: any) => {
        const destinationPin = new markerLib.PinElement({
          background: '#ef4444',
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.2,
        });

        new markerLib.AdvancedMarkerElement({
          map,
          position: destination,
          title: 'Booking Location',
          content: destinationPin.element,
        });
      }).catch((error) => {
        console.error('Error loading marker library:', error);
        // Fallback: try to use old Marker API if AdvancedMarkerElement fails
        if (window.google.maps.Marker) {
          new window.google.maps.Marker({
            map,
            position: destination,
            title: 'Booking Location',
          });
        }
      });
    };

    const initMapAsync = async () => {
      try {
        await loadGoogleMapsLibrary("maps");
        await waitForGoogleMapsMap();
        createMap();
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        toast.error("Failed to load map");
      }
    };
    
    initMapAsync();
  }, [booking, waitForGoogleMapsMap]);

  // Start location tracking when status is en_route
  const { currentLocation: trackedLocation, isTracking, error: locationError } = useFilmmakerLocation({
    bookingId: booking?.id || '',
    filmmakerId: user?.id || '',
    enabled: booking?.status === 'en_route' && !!booking?.id && !!user?.id,
    updateInterval: 15000, // 15 seconds
    minDistanceChange: 50, // 50 meters
    onLocationUpdate: (location) => {
      setCurrentLocation({ lat: location.lat, lng: location.lng });
    },
    onError: (error) => {
      // Only log errors, don't show toast here (hook handles it)
      console.warn('Location tracking error:', error.code, error.message);
    },
  });

  // Update currentLocation from tracked location
  useEffect(() => {
    if (trackedLocation) {
      setCurrentLocation({ lat: trackedLocation.lat, lng: trackedLocation.lng });
    }
  }, [trackedLocation]);

  // Calculate route and ETA when we have both origin and destination
  const destination = booking ? {
    lat: booking.lat || 43.6532,
    lng: booking.lng || -79.3832,
  } : null;

  const { eta, etaMinutes, route, loading: routeLoading } = useRouteCalculation({
    origin: currentLocation,
    destination: destination,
    enabled: booking?.status === 'en_route' && !!currentLocation && !!destination,
    travelMode: 'DRIVING',
  });

  // Update timeLeft based on ETA
  useEffect(() => {
    if (etaMinutes !== null && booking?.status === 'en_route') {
      setTimeLeft(etaMinutes);
    }
  }, [etaMinutes, booking?.status]);

  // Mock timer countdown
  useEffect(() => {
    if (booking?.status === 'en_route') {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 60000); // Decrease every minute
      return () => clearInterval(timer);
    }
  }, [booking?.status]);

  // Polling fallback for arrived status (to detect when customer enters code)
  useEffect(() => {
    if (!booking || booking.status !== 'arrived' || !user?.id || !bookingId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId,
          userId: user.id,
          phone: user.phone
        });
        
        if (!error && data?.booking) {
          const currentBooking = data.booking as Booking;
          if (currentBooking.status !== 'arrived') {
            setBooking(currentBooking);
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Error polling booking status:', error);
      }
    }, 3000); // Poll every 3 seconds
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [booking?.status, booking?.id, user?.id, bookingId]);

  // Auto-open SessionDetailsCard when status is arrived
  useEffect(() => {
    if (booking?.status === 'arrived') {
      setSessionDetailsOpen(true);
    }
  }, [booking?.status]);

  // Calculate real shooting progress based on elapsed time
  useEffect(() => {
    if (booking?.status !== 'in_progress') {
      setElapsedMinutes(0);
      setSessionProgress(0);
      sessionStartTimeRef.current = null; // Reset when not in progress
      return;
    }

    // If we don't have a session start time yet, this is when the session started
    // Prefer session_started_at (most accurate), fallback to updated_at, then current time
    if (!sessionStartTimeRef.current) {
      if (booking.session_started_at) {
        sessionStartTimeRef.current = new Date(booking.session_started_at).getTime();
      } else if (booking.updated_at) {
        sessionStartTimeRef.current = new Date(booking.updated_at).getTime();
      } else {
        sessionStartTimeRef.current = Date.now();
      }
    }
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsedMillis = now - sessionStartTimeRef.current!;
      const elapsedMins = Math.floor(elapsedMillis / 60000);
      
      const totalMinutes = booking?.package?.duration_minutes || 180;
      const newProgress = Math.min((elapsedMins / totalMinutes) * 100, 100);
      
      setElapsedMinutes(elapsedMins);
      setSessionProgress(newProgress);
    }, 1000); // Update every second for real-time accuracy

    return () => clearInterval(interval);
  }, [booking?.status, booking?.package?.duration_minutes, booking?.session_started_at, booking?.updated_at]);

  const handleStatusUpdate = async (newStatus: string) => {
    if (!booking || !user?.id) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error('❌ No active session found. User needs to be authenticated.');
        toast.error('Please log in again to update the booking status');
        return;
      }

      // Prepare update data
      const updateData: any = {};
      
      // Set session_started_at when moving to in_progress
      if (newStatus === 'in_progress' && booking.status !== 'in_progress') {
        updateData.session_started_at = new Date().toISOString();
        console.log('🎬 Recording session start time');
      }
      
      // Set session_finished_at when leaving in_progress
      if (booking.status === 'in_progress' && newStatus !== 'in_progress') {
        updateData.session_finished_at = new Date().toISOString();
      }

      const { data, error } = await invokeFunctionWithAuth('update-booking-status', {
        bookingId: booking.id,
        userId: user.id,
        status: newStatus,
        session_started_at: updateData.session_started_at,
        session_finished_at: updateData.session_finished_at,
      });

      if (error) {
        console.error('Error updating booking status:', error);
        toast.error('Failed to update booking. Please try again.');
      } else {
        // Edge Function returns partial booking data, so we need to fetch complete booking
        // to ensure all related data (package, customer, etc.) is up to date
        try {
          const { data: bookingResponse, error: fetchError } = await invokeFunctionWithAuth('get-booking-details', {
            bookingId: booking.id,
            userId: user.id,
            phone: user.phone
          });
          if (!fetchError && bookingResponse?.booking) {
            const completeBooking = bookingResponse.booking as Booking;
            setBooking(completeBooking);
            toast.success('Booking updated successfully');
          } else {
            console.warn('⚠️ Could not fetch complete booking, using partial update');
            if (data?.booking) {
              setBooking({ ...booking, ...data.booking as Partial<Booking> });
            } else {
              setBooking({ ...booking, status: newStatus as any });
            }
            toast.success('Booking updated successfully');
          }
        } catch (fetchError) {
          console.error('Error fetching complete booking:', fetchError);
          if (data?.booking) {
            setBooking({ ...booking, ...data.booking as Partial<Booking> });
          } else {
            setBooking({ ...booking, status: newStatus as any });
          }
          toast.success(`Status updated to ${newStatus}`);
        }
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking. Please try again.');
    }
  };

  const handleDecline = async () => {
    if (!booking || !user?.id) return;
    
    // Remove filmmaker and revert to paid
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          filmmaker_id: null,
          status: 'paid'
        })
        .eq('id', booking.id)
        .eq('filmmaker_id', user.id);

      if (error) {
        console.error('Error declining booking:', error);
        toast.error('Failed to decline booking');
        return;
      }

      console.log(`📋 Filmmaker ${user.id} declined booking ${booking.id}, triggering auto-assign`);
      
      // Wait a bit for the trigger to create the event (the trigger runs AFTER the update)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update the most recent booking_status_event that has previous_status = 'assigned'
      // This ensures we're updating the correct rejection event, not an initial 'paid' event
      const { data: recentEvents, error: eventError } = await supabase
        .from('booking_status_events')
        .select('id, metadata, created_at')
        .eq('booking_id', booking.id)
        .eq('status', 'paid')
        .order('created_at', { ascending: false })
        .limit(5); // Get last 5 to find the one with previous_status = 'assigned'

      if (!eventError && recentEvents && recentEvents.length > 0) {
        // Find the event that has previous_status = 'assigned' (the rejection event)
        const rejectionEvent = recentEvents.find(event => {
          const metadata = event.metadata as any;
          return metadata?.previous_status === 'assigned';
        });

        if (rejectionEvent) {
          const currentMetadata = (rejectionEvent.metadata as any) || {};
          const updatedMetadata = {
            ...currentMetadata,
            rejected_filmmaker_id: user.id,
            rejection_timestamp: new Date().toISOString()
          };

          const { error: updateError } = await supabase
            .from('booking_status_events')
            .update({ metadata: updatedMetadata })
            .eq('id', rejectionEvent.id);

          if (updateError) {
            console.error('Error updating event metadata:', updateError);
          } else {
            console.log(`✅ Updated event metadata with rejected filmmaker_id: ${user.id}`);
          }
        } else {
          console.warn('⚠️ Could not find rejection event with previous_status = assigned');
        }
      } else if (eventError) {
        console.error('Error fetching events:', eventError);
      }
      
      // Call auto-assign-filmmaker to reassign to next available filmmaker
      const { data: assignData, error: assignError } = await invokeFunctionWithAuth('auto-assign-filmmaker', {
        bookingId: booking.id
      });

      if (assignError) {
        console.error('Error auto-assigning filmmaker after decline:', assignError);
        // Don't show error to user - booking is already declined and will be reassigned later
        toast.success('Booking declined. It will be reassigned to another filmmaker.');
      } else if (assignData?.success) {
        console.log(`✅ Booking ${booking.id} automatically reassigned to filmmaker ${assignData.filmmakerId}`);
        toast.success('Booking declined. It has been reassigned to another filmmaker.');
      } else {
        // No available filmmakers or all rejected
        console.warn(`⚠️ No available filmmakers for booking ${booking.id} after decline`);
        toast.success('Booking declined. It will be reassigned when a filmmaker becomes available.');
      }
      
      // Navigate to dashboard using React Router (preserves session)
      setTimeout(() => {
        navigate('/filmmaker');
      }, 1500);
    } catch (error) {
      console.error('Error declining booking:', error);
      toast.error('Failed to decline booking');
    }
  };

  const getDirections = () => {
    if (booking) {
      const address = `${booking.address_line1}, ${booking.city}, ${booking.postal_code}`;
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    }
  };

  const allDeliverablesCompleted = deliverables.photos && deliverables.video && deliverables.business360;

  // Format time helper
  const formatTime = (dateStr: string | Date) => {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading job details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Job not found</p>
          <Link to="/filmmaker">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Calculate real-time remaining minutes for in_progress status
  const totalMinutes = booking.package?.duration_minutes || 180;
  const actualElapsedMinutes = booking.status === 'in_progress' ? elapsedMinutes : 0;
  const remainingMinutes = Math.max(totalMinutes - actualElapsedMinutes, 0);
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;

  // Calculate estimated end time based on session start + package duration
  const sessionStartTime = booking.session_started_at 
    ? new Date(booking.session_started_at) 
    : new Date(booking.scheduled_at);
  const estimatedEndTime = new Date(sessionStartTime.getTime() + totalMinutes * 60000);

  const getCurrentStep = () => {
    switch (booking.status) {
      case 'assigned':
        return 0;
      case 'en_route':
        return 0;
      case 'arrived':
      case 'in_progress':
        return 1;
      case 'awaiting_finish_confirm':
      case 'done_waiting_upload':
        return 2;
      case 'uploaded':
      case 'completed':
        return 2; // Also step 2 since Shooting Done is no longer part of the active flow
      default:
        return 0;
    }
  };

  const getStepTitle = () => {
    switch (booking.status) {
      case 'assigned':
        return 'Review Booking';
      case 'en_route':
        return '27th Oct Shooting';
      case 'arrived':
      case 'in_progress':
        return '27th Oct Shooting';
      case 'awaiting_finish_confirm':
      case 'done_waiting_upload':
      case 'uploaded':
      case 'completed':
        return '27th Oct Shooting';
      default:
        return 'Job Details';
    }
  };

  const getStepIcon = () => {
    switch (booking.status) {
      case 'en_route':
        return <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
          <Camera className="w-7 h-7 text-destructive" />
        </div>;
      case 'arrived':
      case 'in_progress':
        return <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
          <Camera className="w-7 h-7 text-destructive" />
        </div>;
      case 'awaiting_finish_confirm':
        return <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center">
          <CheckCircle className="w-7 h-7 text-orange-500" />
        </div>;
      case 'done_waiting_upload':
      case 'uploaded':
      case 'completed':
        return <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
          <Check className="w-7 h-7 text-destructive" />
        </div>;
      default:
        return null;
    }
  };

  const getStepSubtitle = () => {
    switch (booking.status) {
      case 'en_route':
        return 'Go to the Client';
      case 'arrived':
      case 'in_progress':
        return 'Do Your Magic';
      case 'awaiting_finish_confirm':
        return 'Awaiting Customer Confirmation';
      case 'done_waiting_upload':
      case 'uploaded':
      case 'completed':
        return '';
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (booking.status) {
      case 'en_route':
        return 'The Shooting has been confirmed and John Doe is waiting for you at his address.';
      case 'arrived':
        return "It's time to get creative! Do your shooting and make sure to do your best.";
      case 'in_progress':
        return "It's time to get creative! Do your shooting and make sure to do your best.";
      case 'awaiting_finish_confirm':
        return 'The customer has completed the shooting session. Please wait for them to confirm completion before uploading your footage.';
      default:
        return '';
    }
  };

  // Session Details Component (reusable)
  const SessionDetailsCard = ({ showEarnings = false, defaultOpen = false }: { showEarnings?: boolean; defaultOpen?: boolean }) => {
    useEffect(() => {
      if (defaultOpen) {
        setSessionDetailsOpen(true);
      }
    }, [defaultOpen]);
    
    return (
    <Collapsible open={sessionDetailsOpen} onOpenChange={setSessionDetailsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <h3 className="font-semibold">Session Details</h3>
          <ChevronDown className={`h-5 w-5 transition-transform ${sessionDetailsOpen ? 'rotate-180' : ''}`} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-4 space-y-4 px-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Camera className="h-5 w-5 text-destructive" />
              <span className="text-sm text-muted-foreground">Package</span>
            </div>
            <span className="text-sm font-medium">{booking.package?.name}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-destructive" />
              <span className="text-sm text-muted-foreground">Date</span>
            </div>
            <span className="text-sm font-medium">
              {format(new Date(booking.scheduled_at), 'MMM dd, yyyy')}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-destructive" />
              <span className="text-sm text-muted-foreground">Time</span>
            </div>
            <span className="text-sm font-medium">
              {format(new Date(booking.scheduled_at), 'HH:mm')} - {format(new Date(new Date(booking.scheduled_at).getTime() + (booking.package?.duration_minutes || 0) * 60000), 'HH:mm')}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-destructive" />
              <span className="text-sm text-muted-foreground">Location</span>
            </div>
            <span className="text-sm font-medium text-right">
              {booking.address_line1}, {booking.city}, {booking.postal_code}
            </span>
          </div>

          {showEarnings && (
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-3">
                <span className="text-sm text-destructive font-bold">$</span>
                <span className="text-sm text-muted-foreground">Earnings</span>
              </div>
              <span className="text-sm font-bold">${(booking.total_cents / 100).toFixed(2)}</span>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center pt-2">
            Need Help? <button className="text-destructive underline">Contact Our Human Support</button>
          </p>
        </div>
      </CollapsibleContent>
    </Collapsible>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <PageHeader variant="back" title={getStepTitle()} backTo="/filmmaker" />

      <div className="px-4 py-6 space-y-6">
        {/* Review Booking - Status: assigned */}
        {booking.status === 'assigned' && (
          <>
            <div>
              <h1 className="text-2xl font-bold mb-6">Review Booking</h1>

              {/* Customer Card */}
              <Card className="p-5 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                    <User className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{booking.customer?.full_name || 'Customer'}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className="w-4 h-4 fill-destructive text-destructive" />
                      ))}
                      <span className="text-sm text-muted-foreground ml-2">4.9/5.0</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">12 Shootings Made</p>
                  </div>
                </div>
              </Card>

              {/* Session Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-4">Session Details</h3>
                <Card className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Camera className="h-5 w-5 text-destructive" />
                      <span className="text-sm text-muted-foreground">Package</span>
                    </div>
                    <span className="text-sm font-medium">{booking.package?.name}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-destructive" />
                      <span className="text-sm text-muted-foreground">Date</span>
                    </div>
                    <span className="text-sm font-medium">
                      {format(new Date(booking.scheduled_at), 'MMM dd, yyyy')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-destructive" />
                      <span className="text-sm text-muted-foreground">Time</span>
                    </div>
                    <span className="text-sm font-medium">
                      {format(new Date(booking.scheduled_at), 'HH:mm')} - {format(new Date(new Date(booking.scheduled_at).getTime() + (booking.package?.duration_minutes || 0) * 60000), 'HH:mm')}
                    </span>
                  </div>

                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-destructive flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Location</span>
                    </div>
                    <span className="text-sm font-medium text-right">
                      {booking.address_line1}, {booking.city}, {booking.postal_code}
                    </span>
                  </div>

                  {/* Google Maps */}
                  <div ref={mapRefAssigned} className="w-full h-64 rounded-lg overflow-hidden border" />

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-destructive" />
                      <span className="text-sm text-muted-foreground">Additional Notes</span>
                    </div>
                    <span className="text-sm font-medium">{booking.notes || 'None'}</span>
                  </div>
                </Card>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between px-4 py-2 bg-muted/30 rounded-lg">
                <span className="text-sm text-muted-foreground">You Will Receive</span>
                <span className="text-lg font-bold">${(booking.total_cents / 100).toFixed(2)}</span>
              </div>

              <Button 
                className="w-full h-14 text-base font-semibold bg-destructive hover:bg-destructive/90"
                onClick={() => handleStatusUpdate('en_route')}
              >
                Accept Shooting
              </Button>

              <Button 
                variant="ghost" 
                className="w-full h-12 text-base font-semibold"
                onClick={handleDecline}
              >
                Decline Shooting
              </Button>

            </div>
          </>
        )}

        {/* Step 1 - Go to the Client - Status: en_route */}
        {booking.status === 'en_route' && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-destructive" />
                </div>
                <h2 className="text-xl font-bold">{getStepSubtitle()}</h2>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Step {getCurrentStep() + 1} of 3</p>
                <StepProgressBar currentStep={getCurrentStep() + 1} totalSteps={3} className="h-2 mb-4" />
                <p className="text-sm text-muted-foreground">
                  {getStepDescription()}
                </p>
              </div>
            </div>

            {/* Timer Card */}
            <Card className="p-6">
              <h3 className="text-2xl font-bold mb-4">Your Client is Waiting</h3>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">Time Left</span>
                <span className="text-3xl font-bold">
                  {etaMinutes !== null ? etaMinutes : timeLeft} mins
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                <Clock className="w-4 h-4 text-destructive" />
                <span>
                  {eta 
                    ? `Arriving at ${format(eta, 'h:mm a')}`
                    : `Arrival due at ${format(new Date(booking.scheduled_at), 'h:mm a')}`
                  }
                </span>
              </div>

              {/* Dynamic Map with real-time tracking */}
              {destination && (
                <DynamicMap
                  destination={destination}
                  filmmakerLocation={currentLocation}
                  mode="filmmaker"
                  className="mb-4"
                  height="300px"
                />
              )}

              {locationError && locationError.code === locationError.PERMISSION_DENIED && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-600">
                    Permissão de localização negada. Por favor, permita o acesso à localização nas configurações do navegador para rastreamento em tempo real.
                  </p>
                </div>
              )}

              {locationError && locationError.code === locationError.POSITION_UNAVAILABLE && (
                <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-sm text-orange-600">
                    GPS indisponível. Verifique se o GPS está ativado no dispositivo.
                  </p>
                </div>
              )}

              {!isTracking && booking.status === 'en_route' && !locationError && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-600">
                    Iniciando rastreamento de localização...
                  </p>
                </div>
              )}

              {locationError && locationError.code === locationError.TIMEOUT && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-600">
                    Aguardando localização GPS... Isso pode levar alguns segundos.
                  </p>
                </div>
              )}

              <Button 
                variant="outline"
                className="w-full h-12 mb-4"
                onClick={getDirections}
              >
                <Navigation className="w-4 h-4 mr-2" />
                Open on Maps
              </Button>

              {/* Customer Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                  <User className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{booking.customer?.full_name || 'John Doe'}</h4>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-3 h-3 fill-destructive text-destructive" />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">4.9/5.0</span>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 rounded">Arrived</span>
              </div>
            </Card>

            <Button 
              variant="outline"
              className="w-full h-12"
            >
              Chat with Client
            </Button>

            <SessionDetailsCard showEarnings={true} />

            {/* Actions */}
            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-semibold bg-destructive hover:bg-destructive/90"
                onClick={() => handleStatusUpdate('arrived')}
              >
                Confirm Arrival
              </Button>

              <Button 
                variant="ghost" 
                className="w-full h-12 text-base font-semibold"
              >
                Cancel Shooting
              </Button>

            </div>
          </>
        )}

        {/* Step 2 - Awaiting Customer Confirmation - Status: arrived */}
        {booking.status === 'arrived' && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-xl font-bold">Awaiting Customer Confirmation</h2>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Step 2 of 3</p>
                <StepProgressBar currentStep={2} totalSteps={3} className="h-2 mb-4" />
                <p className="text-sm text-muted-foreground">
                  Provide the code below to the customer so they can confirm your identity.
                </p>
              </div>
            </div>

            {/* Verification Code */}
            {booking.verification_code && (
              <div className="mb-6">
                <div className="flex items-start justify-center gap-2 mb-4">
                  <div className="flex flex-col items-start">
                    <h3 className="text-base font-bold text-foreground mb-4">Verification Code</h3>
                    <div className="flex items-center gap-2">
                      {booking.verification_code.split('').map((digit, idx) => (
                        <div
                          key={idx}
                          className="w-12 h-14 rounded border border-border bg-background flex items-center justify-center text-2xl font-semibold text-foreground"
                        >
                          {digit}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Provide this code to the customer to confirm your identity before starting the shooting.
                </p>
              </div>
            )}

            <Card className="p-5 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Waiting for Customer</p>
                  <p className="text-xs text-muted-foreground">
                    The customer needs to enter the code in their app to confirm your identity before starting the shooting.
                  </p>
                </div>
              </div>
            </Card>

            <SessionDetailsCard showEarnings={true} defaultOpen={true} />

            <Button 
              variant="outline"
              className="w-full h-12"
              disabled={true}
            >
              Chat with Client
            </Button>
          </>
        )}

        {/* Step 2 - Do Your Magic - Status: in_progress */}
        {booking.status === 'in_progress' && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-destructive" />
                </div>
                <h2 className="text-xl font-bold">{getStepSubtitle()}</h2>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Step {getCurrentStep() + 1} of 3</p>
                <StepProgressBar currentStep={getCurrentStep() + 1} totalSteps={3} className="h-2 mb-4" />
                <p className="text-sm text-muted-foreground">
                  {getStepDescription()}
                </p>
              </div>
            </div>

            {/* Shooting Progress Card */}
            <Card className="p-6">
              <h3 className="text-2xl font-bold mb-6 text-center">Shooting in Progress</h3>

              {/* Circular Timer */}
              <div className="flex flex-col items-center justify-center mb-6">
                <div className="relative w-48 h-48">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 88}`}
                      strokeDashoffset={`${2 * Math.PI * 88 * (1 - sessionProgress / 100)}`}
                      className="text-destructive transition-all duration-1000"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold">
                      {hours > 0 ? `${hours} Hour${hours > 1 ? 's' : ''}` : `${minutes} min`}
                    </span>
                    <span className="text-sm text-muted-foreground">Time Left</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4 w-full mt-4 text-xs text-muted-foreground">
                  <span>Started at {formatTime(sessionStartTime)}</span>
                  <div className="h-px w-12 bg-muted-foreground/30" />
                  <span>Finishing at {formatTime(estimatedEndTime)}</span>
                </div>
              </div>

              {/* Deliverables Checklist */}
              <div className="space-y-3 mb-6">
                <h4 className="font-semibold">Deliverables Checklist</h4>
                
                <button
                  onClick={() => setDeliverables({ ...deliverables, photos: !deliverables.photos })}
                  className={`w-full p-4 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                    deliverables.photos 
                      ? 'border-destructive bg-destructive/5' 
                      : 'border-muted bg-background'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    deliverables.photos ? 'border-destructive bg-destructive' : 'border-muted'
                  }`}>
                    {deliverables.photos && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="font-medium">3 Photos</span>
                </button>

                <button
                  onClick={() => setDeliverables({ ...deliverables, video: !deliverables.video })}
                  className={`w-full p-4 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                    deliverables.video 
                      ? 'border-destructive bg-destructive/5' 
                      : 'border-muted bg-background'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    deliverables.video ? 'border-destructive bg-destructive' : 'border-muted'
                  }`}>
                    {deliverables.video && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="font-medium">1 Video</span>
                </button>

                <button
                  onClick={() => setDeliverables({ ...deliverables, business360: !deliverables.business360 })}
                  className={`w-full p-4 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                    deliverables.business360 
                      ? 'border-destructive bg-destructive/5' 
                      : 'border-muted bg-background'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    deliverables.business360 ? 'border-destructive bg-destructive' : 'border-muted'
                  }`}>
                    {deliverables.business360 && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="font-medium">1 Business 360°</span>
                </button>
              </div>

              {/* Customer Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                  <User className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{booking.customer?.full_name || 'John Doe'}</h4>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-3 h-3 fill-destructive text-destructive" />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">4.9/5.0</span>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 rounded">Arrived</span>
              </div>
            </Card>

            <Button 
              variant="outline"
              className="w-full h-12"
            >
              Chat with Client
            </Button>

            <SessionDetailsCard showEarnings={true} />

            {/* Actions */}
            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-semibold bg-destructive hover:bg-destructive/90"
                onClick={() => handleStatusUpdate('done_waiting_upload')}
                disabled={!allDeliverablesCompleted}
              >
                Finish Shooting
              </Button>

              <Button 
                variant="ghost" 
                className="w-full h-12 text-base font-semibold"
              >
                {booking.status === 'in_progress' ? 'Ask to Finish Earlier' : 'Cancel Shooting'}
              </Button>

                         </div>
           </>
         )}

         {/* Step 2.5 - Awaiting Customer Confirmation - Status: awaiting_finish_confirm */}
         {booking.status === 'awaiting_finish_confirm' && (
           <>
             <div className="mb-6">
               <div className="flex items-center gap-3 mb-4">
                 <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                   <CheckCircle className="w-5 h-5 text-orange-500" />
                 </div>
                 <h2 className="text-xl font-bold">Awaiting Customer Confirmation</h2>
               </div>
               <div className="text-center">
                 <p className="text-sm text-muted-foreground mb-2">Step {getCurrentStep() + 1} of 3</p>
                 <StepProgressBar currentStep={getCurrentStep() + 1} totalSteps={3} className="h-2 mb-4" />
                 <p className="text-muted-foreground mb-6">
                   The customer has completed the shooting session. Please wait for them to confirm completion before uploading your footage.
                 </p>
                 
                 <Card className="p-6 space-y-4 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900">
                   <div className="flex items-center justify-center">
                     <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                       <CheckCircle className="w-8 h-8 text-orange-500" />
                     </div>
                   </div>
                   <h3 className="text-lg font-bold text-center">Shooting Completed by Customer</h3>
                   <p className="text-sm text-muted-foreground text-center">
                     The customer has marked the shooting session as complete. Click the button below to confirm and proceed to upload.
                   </p>
                 </Card>
               </div>
             </div>

             <SessionDetailsCard showEarnings={true} />

             <div className="space-y-3">
               <Button 
                 className="w-full h-14 text-base font-semibold bg-destructive hover:bg-destructive/90"
                 onClick={() => handleStatusUpdate('uploaded')}
               >
                 Confirm and Proceed to Upload
               </Button>
             </div>
           </>
         )}

         {/* Step 3 - Upload the Shooting Material - Status: done_waiting_upload */}
         {booking.status === 'done_waiting_upload' && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-destructive" />
                </div>
                <h2 className="text-xl font-bold">Upload the Shooting Material</h2>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Step {getCurrentStep() + 1} of 3</p>
                <StepProgressBar currentStep={getCurrentStep() + 1} totalSteps={3} className="h-2 mb-4" />
                <p className="text-sm text-muted-foreground">
                  Your shooting has been completed. Now to finish off the booking, upload the shooting material for the client.
                </p>
              </div>
            </div>

            {/* Shooting Deliverables Card */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Shooting Deliverables</h3>
              
              {/* Upload Area - Illustrative */}
              <div className="border-2 border-dashed border-muted rounded-lg p-12 text-center mb-6 bg-muted/10">
                <Upload className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h4 className="text-2xl font-bold mb-2">Upload the Material</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Compress the shooting files into a .zip folder and upload them here. Organize the content in folders.
                </p>
              </div>

              {/* Client Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                  <User className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{booking.customer?.full_name || 'Customer'}</h4>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-3 h-3 fill-destructive text-destructive" />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">4.9/5.0</span>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 rounded">Arrived</span>
              </div>
            </Card>

            <Button 
              variant="outline"
              className="w-full h-12 mb-4"
            >
              Chat with Client
            </Button>

            <SessionDetailsCard showEarnings={true} />

            {/* Actions - For now just show completion, actual upload will come later */}
            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-semibold bg-destructive hover:bg-destructive/90"
                onClick={() => handleStatusUpdate('uploaded')}
              >
                Complete Upload ✓
              </Button>

            </div>
          </>
        )}

        {/* Step 3 Complete - Shooting Done - Status: uploaded, completed */}
        {(booking.status === 'uploaded' || booking.status === 'completed') && (
          <>
            <div className="text-center space-y-4 mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center">
                  <Check className="h-12 w-12 text-destructive" />
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold mb-2">Shooting Done</h2>
                <p className="text-sm text-muted-foreground">
                  Finished {format(new Date(booking.scheduled_at), 'MMM d')} at {format(new Date(), 'h:mm a')}
                </p>
              </div>
            </div>

            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-center">Content Uploaded</h2>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                  <img 
                    src="https://images.unsplash.com/photo-1600891964092-4316c288032e?w=400&h=225&fit=crop"
                    alt="Uploaded content"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Camera className="h-3 w-3" />
                    <span>View 12 Images</span>
                  </div>
                </div>
                
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                  <img 
                    src="https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=400&h=225&fit=crop"
                    alt="Uploaded content"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Video className="h-3 w-3" />
                    <span>View 3 Videos</span>
                  </div>
                </div>
              </div>
            </Card>

            <div className="space-y-3">
              <Button 
                className="w-full h-14 text-base font-bold rounded-xl shadow-lg"
              >
                <Download className="h-5 w-5 mr-2" />
                Download All
              </Button>

              <Button 
                variant="outline"
                className="w-full h-12 text-base font-semibold"
              >
                <Star className="h-5 w-5 mr-2" />
                Review the Client
              </Button>
            </div>

            {/* Note from Customer */}
            {booking.customer && (
              <Card className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-full bg-muted flex-shrink-0"></div>
                  <div className="flex-1">
                    <h3 className="font-bold mb-1">A Note From {booking.customer?.full_name || 'Customer'}</h3>
                    <p className="text-sm text-muted-foreground">
                      "Magnis turpis vestibulum pellentesque feugiat dui quis mattis arcu. Tincidunt quam in condimentum risus facilisis commodo. Iaculis feugiat quam volutpat aenean massa. Vulputate in ut fames velit. Quis nulla leo tristique ultrices. Mi amet iaculis posuere amet."
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <SessionDetailsCard showEarnings={true} />
          </>
        )}
      </div>
    </div>
  );
}
